import React, { useState } from 'react';
import dayjs from 'dayjs';
import { formatEther } from 'ethers/lib/utils';

import FancyContainer from 'common/components-v2/Markup/FancyContainer/FancyContainer';
import ChartTabs from 'common/components-v2/Chart/Tabs/Tabs';
import LineChart from 'common/components-v2/Chart/Line/LineChart';
import useTimeSeries, {
  boundsFromNow
} from 'common/components-v2/Chart/useTimeSeries';
import { AccountStakeQueryResult } from 'common/hooks/useAccountStakeQuery';
import { formatNumber } from 'forta-app/lib/utils';
import {
  CHART_INTERVAL,
  CHART_INTERVAL_OPTIONS
} from 'common/components-v2/Chart/constants';

enum ChartType {
  ClaimedRewards = 'rewards'
}

const CHART_OPTIONS = [
  { label: 'Claimed Rewards', value: ChartType.ClaimedRewards }
];

type ClaimedRewardsChartProps = {
  stake?: AccountStakeQueryResult;
  loading?: boolean;
  className?: string;
};

function ClaimedRewardsChart(props: ClaimedRewardsChartProps): JSX.Element {
  const { stake, loading, className } = props;

  const [interval, setInterval] = useState(CHART_INTERVAL.OneWeek);
  const [chartType, setChartType] = useState(ChartType.ClaimedRewards);

  const seriesData = useTimeSeries(stake?.claimedRewards, {
    xAccessor: 'timestamp',
    yAccessor: 'amount',
    timeframe: 'day',
    cumulativeSum: true,
    bounds: boundsFromNow({ interval }),
    prepare: (d) => ({
      ...d,
      amount: Number(formatEther(d.amount.toString(10)))
    }),
    deps: [interval]
  });

  return (
    <FancyContainer variant="gradient" className={className}>
      <FancyContainer.Filters>
        <ChartTabs
          separator
          name="chart-type"
          value={chartType}
          items={CHART_OPTIONS}
          onChange={(e) => setChartType(e.target.value)}
        />
        <ChartTabs
          name="interval"
          value={interval}
          items={CHART_INTERVAL_OPTIONS}
          onChange={(e) => setInterval(e.target.value)}
        />
      </FancyContainer.Filters>
      <FancyContainer.FullWidth>
        <LineChart
          type="time"
          data={seriesData}
          empty={seriesData.length === 0}
          xAccessor="timestamp"
          loading={loading}
          lines={[
            {
              label: 'Stake',
              yAccessor: 'amount',
              formatter: (v) => `${formatNumber(v)} FORT`
            }
          ]}
          tooltipTitle={(v) => dayjs.unix(Number(v)).format('MMM D, YYYY')}
        />
      </FancyContainer.FullWidth>
    </FancyContainer>
  );
}

export default ClaimedRewardsChart;
